// Background music + SFX controller, built on Howler.
//
// Behavior:
//   - Music respects the `music` setting in data/settings.js.
//   - Tries to autoplay on init; browsers that block autoplay until a user
//     gesture are handled by Howler's autoUnlock + a one-shot listener that
//     starts the track on the first interaction.
//   - Loops indefinitely. 50% volume in menu states, 20% in the GameState.
//     Volume changes fade across 1 second.
//   - Toggle in Settings instantly stops or restarts the track.
//   - SFX respect the `sfx` setting; playSfx() short-circuits when disabled.
//     Howl instances are lazily created and cached per name.

import { Howl, Howler } from 'howler';
import { getSettings } from './settings.js';

const MUSIC_URL = '/assets/FutureTechnologyRetro.mp3';

export const MUSIC_VOLUME_NORMAL = 0.5;
export const MUSIC_VOLUME_DUCKED = 0.2;
const FADE_MS = 3000;

// SFX registry — name → URL. Add new entries here and reference them via the
// exported SFX_* constants below. Spaces in filenames must be URL-encoded
// (%20) so Web Audio's XHR fetch doesn't 404 on path resolution.
const SFX_URLS = {
    button:        '/assets/sfx/Beep%205.mp3',
    premiumButton: '/assets/sfx/Beep%20Rise%201.mp3',
    cellClick:     '/assets/sfx/Digital%20Click%201.mp3',
    mineHit:       '/assets/sfx/Negative.mp3',
    floodReveal:   '/assets/sfx/Digital%20Line%202.mp3',
    win:           '/assets/sfx/Beep%20Rise%204.mp3',
    gameOver:      '/assets/sfx/Transition%202.mp3',
};

export const SFX_BUTTON = 'button';
export const SFX_PREMIUM_BUTTON = 'premiumButton';
export const SFX_CELL_CLICK = 'cellClick';
export const SFX_MINE_HIT = 'mineHit';
export const SFX_FLOOD_REVEAL = 'floodReveal';
export const SFX_WIN = 'win';
export const SFX_GAME_OVER = 'gameOver';

const SFX_VOLUME = 0.6;
const sfxCache = new Map();

function getSfx( name )
{
    let sfx = sfxCache.get( name );
    if ( sfx ) return sfx;
    const url = SFX_URLS[ name ];
    if ( !url ) return null;
    sfx = new Howl({
        src:     [ url ],
        volume:  SFX_VOLUME,
        preload: true,
        html5:   false,
    });
    sfxCache.set( name, sfx );
    return sfx;
}

let music = null;
let targetVolume = MUSIC_VOLUME_NORMAL;
let unlockListenersBound = false;
let wasPlayingBeforeHidden = false;
let lifecycleListenersBound = false;

function getMusic()
{
    if ( music ) return music;
    music = new Howl({
        src:    [ MUSIC_URL ],
        loop:   true,
        volume: targetVolume,
        // Web Audio (default) gives us proper fades. The file is small enough
        // that decoding the whole thing is fine.
        html5:  false,
    });
    return music;
}

// Silence ALL audio when the page is hidden (tab switched, window minimized,
// PWA backgrounded) and resume on return — but only if music was playing
// before the page went hidden, so a user-disabled track doesn't sneak back on.
//
// Three layers of silence on hidden, because pause() alone doesn't always
// stop output for an html5: false (Web Audio) Howl in background tabs:
//   1. music.stop()            — fully stops the buffer source. (We don't
//                                preserve playback position; the track is
//                                ambient and restarting on return is fine.)
//   2. Howler.mute( true )     — global master mute, instant silence on every
//                                Howl regardless of internal state.
//   3. Howler.ctx.suspend()    — suspends the shared AudioContext, halting
//                                output at the engine level.
//
// pagehide is the last reliable point to silence audio before the page is
// torn down: most platforms tear down the JS context anyway, but some PWA
// configurations keep audio threads alive briefly after the window closes.
function bindLifecycleListenersOnce()
{
    if ( lifecycleListenersBound ) return;
    lifecycleListenersBound = true;

    const goInactive = () =>
    {
        if ( music )
        {
            if ( music.playing() ) wasPlayingBeforeHidden = true;
            try { music.stop(); } catch {}
        }
        try { Howler.mute( true ); } catch {}
        try { Howler.ctx?.suspend?.(); } catch {}
    };

    const goActive = () =>
    {
        try { Howler.ctx?.resume?.(); } catch {}
        try { Howler.mute( false ); } catch {}
        if ( wasPlayingBeforeHidden && getSettings().music )
        {
            const m = getMusic();
            if ( !m.playing() ) m.play();
        }
        wasPlayingBeforeHidden = false;
    };

    document.addEventListener( 'visibilitychange', () =>
    {
        if ( document.hidden ) goInactive(); else goActive();
    });

    const teardown = () =>
    {
        if ( music )
        {
            try { music.stop(); } catch {}
        }
    };
    window.addEventListener( 'pagehide', teardown );
    window.addEventListener( 'beforeunload', teardown );
}

function bindUnlockOnce()
{
    if ( unlockListenersBound ) return;
    unlockListenersBound = true;

    const events = [ 'pointerdown', 'touchstart', 'keydown' ];
    const onGesture = () =>
    {
        events.forEach( e => window.removeEventListener( e, onGesture, true ) );
        try { Howler.ctx?.resume?.(); } catch {}
        if ( getSettings().music )
        {
            const m = getMusic();
            if ( !m.playing() ) m.play();
        }
    };
    events.forEach( e => window.addEventListener( e, onGesture, { capture: true, passive: true } ) );
}

/**
 * Boot the audio system. Call once at app start. Starts the music track if
 * the user has it enabled in Settings. If autoplay is blocked, defers the
 * actual playback to the first user interaction.
 */
export function initAudio()
{
    bindLifecycleListenersOnce();
    if ( !getSettings().music ) return;
    const m = getMusic();
    m.play();
    // If the audio context is suspended (autoplay blocked), wait for a gesture.
    if ( Howler.ctx && Howler.ctx.state !== 'running' )
    {
        bindUnlockOnce();
    }
}

/**
 * Fade music to the given volume (0..1) over the given duration (ms).
 * No-op if music is disabled in settings or hasn't been initialized.
 */
export function setMusicVolume( volume, fadeMs = FADE_MS )
{
    targetVolume = volume;
    if ( !music ) return;
    if ( music.playing() )
    {
        music.fade( music.volume(), volume, fadeMs );
    }
    else
    {
        music.volume( volume );
    }
}

/**
 * Eagerly create and start downloading Howl instances for every entry in
 * SFX_URLS. Call once at app start so that by the time the user triggers an
 * effect, the buffer is already decoded and play() is instant.
 */
export function preloadSfx()
{
    for ( const name of Object.keys( SFX_URLS ) )
    {
        getSfx( name );
    }
}

/**
 * Play a one-shot sound effect by registry name (e.g. SFX_BUTTON). Returns
 * the Howler sound id so callers can stop a specific instance later, or null
 * when SFX are disabled / name is unknown. The audio context is shared with
 * the music track, so Howler's autoUnlock already covers SFX on the first
 * user gesture.
 */
export function playSfx( name )
{
    if ( !getSettings().sfx ) return null;
    const sfx = getSfx( name );
    if ( !sfx ) return null;

    try { Howler.ctx?.resume?.(); } catch {}
    return sfx.play();
}

/**
 * Stop a sound effect that may currently be playing. Stops every active
 * instance of the given name (or just one, if a sound id is provided).
 * No-op when the name has never been played.
 */
export function stopSfx( name, id )
{
    const sfx = sfxCache.get( name );
    if ( !sfx ) return;
    try { ( id !== undefined ) ? sfx.stop( id ) : sfx.stop(); } catch {}
}

/**
 * React to a Settings toggle change. When enabled, start playing at the
 * current target volume; when disabled, fade out and stop.
 */
export function setMusicEnabled( enabled )
{
    if ( enabled )
    {
        const m = getMusic();
        m.volume( targetVolume );
        if ( !m.playing() ) m.play();
        if ( Howler.ctx && Howler.ctx.state !== 'running' ) bindUnlockOnce();
    }
    else if ( music )
    {
        const from = music.volume();
        music.fade( from, 0, 200 );
        setTimeout( () =>
        {
            if ( music )
            {
                music.stop();
                music.volume( targetVolume );
            }
        }, 220 );
    }
}
